import React, { useState, useEffect } from 'react';
import { withRouter } from '../../common/withRouter';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Notification,
  ButtonOBSOLETED,
  Loader,
  Message
} from '../../web.common';
import withApi from '../higherOrderComponents/withApi';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';

const FacilityRedirectAddInternal = ({
  api,
  history,
  match
}) => {
  const redirectId = match.params.redirectId;
  const isEditMode = !!redirectId;

  const initialValue = {
    SourceSlug: '',
    TargetSlug: '',
    TargetUrl: '',
    Type: '1',
    ProductSlug: '',
    IsActive: true
  };

  const [formData, setFormData] = useState(initialValue);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      loadRedirectData();
    }
  }, [redirectId]);

  // Reset fields when redirect type changes
  useEffect(() => {
    const type = parseInt(formData.Type);

    setFormData(prevData => {
      const newData = { ...prevData };

      // Type 1 (FacilitySlugRename): only needs TargetSlug
      if (type === 1) {
        newData.TargetUrl = '';
        newData.ProductSlug = '';
      }

      // Type 2 (ProductSlugRename): SourceSlug is the old product slug and TargetSlug
      // the new one; the resolver ignores ProductSlug.
      if (type === 2) {
        newData.TargetUrl = '';
        newData.ProductSlug = '';
      }

      // Type 3 (FullUrlRedirect): needs TargetUrl and a ProductSlug ("*" = every product)
      if (type === 3) {
        newData.TargetSlug = '';
        if (!newData.ProductSlug) {
          newData.ProductSlug = '*';
        }
      }

      return newData;
    });
  }, [formData.Type]);

  const loadRedirectData = async () => {
    try {
      const redirect = await api.facilityRedirects.getFacilityRedirect(redirectId);

      // Map the type string to numeric value
      let typeValue = '1';
      if (redirect.type === 'ProductSlugRename') typeValue = '2';
      else if (redirect.type === 'FullUrlRedirect') typeValue = '3';

      setFormData({
        SourceSlug: redirect.sourceSlug || '',
        TargetSlug: redirect.targetSlug || '',
        TargetUrl: redirect.targetUrl || '',
        Type: typeValue,
        ProductSlug: redirect.productSlug || '',
        IsActive: redirect.isActive
      });
      setIsLoading(false);
    } catch (e) {
      setNotification(new Message('error', 'Unable to load redirect data'));
      setIsLoading(false);
    }
  };

  const formLabelStyle = {
    display: "table-cell",
    width: "20%",
    whiteSpace: "nowrap",
    fontWeight: "bold",
    verticalAlign: "top"
  };

  const formSpanStyle = {
    display: "table-cell",
  };

  const rowStyle = {
    display: "table",
    width: "100%",
    margin: "auto",
    marginBottom: "0"
  };

  const containerStyle = {
    width: '80%',
    margin: 'auto',
    backgroundColor: 'white',
    border: "1px solid #e5e5e5",
    padding: "1.2rem"
  };

  const helpTextStyle = {
    fontSize: '0.85rem',
    color: '#666',
    fontStyle: 'italic',
    marginTop: '5px'
  };

  const validateForm = () => {
    const emptyFields = [];
    let response = {
      success: false,
      type: 'error',
      message: ''
    };

    if (formData.SourceSlug.trim() === '') {
      emptyFields.push('Source Slug');
    }

    const type = parseInt(formData.Type);

    // Type 1 (FacilitySlugRename) requires TargetSlug
    if (type === 1 && formData.TargetSlug.trim() === '') {
      response.message = 'Target Slug is required for Facility Slug Rename type.';
      return response;
    }

    // Type 2 (ProductSlugRename) requires TargetSlug (the new product slug)
    if (type === 2 && formData.TargetSlug.trim() === '') {
      response.message = 'Target Slug is required for Product Slug Rename type.';
      return response;
    }

    if ((type === 1 || type === 2) && formData.TargetSlug.trim() === formData.SourceSlug.trim()) {
      response.message = 'Target Slug must differ from Source Slug.';
      return response;
    }

    // Type 3 (FullUrlRedirect) requires a relative TargetUrl and a ProductSlug
    if (type === 3) {
      const targetUrl = formData.TargetUrl.trim();
      if (targetUrl === '') {
        response.message = 'Target URL is required for Full URL Redirect type.';
        return response;
      }
      if (targetUrl.includes('://') || targetUrl.startsWith('/')) {
        response.message = 'Target URL must be a path without domain and without a leading "/", e.g. copenhagen/padel.';
        return response;
      }
      if (formData.ProductSlug.trim() === '') {
        response.message = 'Product Slug is required for Full URL Redirect type: use "*" for every product.';
        return response;
      }
    }

    if (emptyFields.length === 1) {
      response.message = `Unable to save. The ${emptyFields[0]} field is mandatory.`;
    } else if (emptyFields.length > 1) {
      response.message = `Unable to save. ${emptyFields.join(', ')} are mandatory.`;
    } else {
      response.success = true;
      response.type = 'success';
    }

    return response;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationResponse = validateForm();

    if (!validationResponse.success) {
      setNotification(new Message(validationResponse.type, validationResponse.message));
      return;
    }

    try {
      const payload = {
        SourceSlug: formData.SourceSlug.trim(),
        TargetSlug: formData.TargetSlug.trim() || null,
        TargetUrl: formData.TargetUrl.trim() || null,
        Type: parseInt(formData.Type),
        ProductSlug: formData.ProductSlug.trim() || null,
        IsActive: formData.IsActive
      };

      if (isEditMode) {
        payload.Id = redirectId;
        const result = await api.facilityRedirects.updateFacilityRedirect(payload);
        if (result.success === false) {
          setNotification(new Message('error', result.errorMessage));
          return;
        }
        setNotification(new Message('success', 'Redirect updated successfully. Remember to restart the application.'));
        setTimeout(() => {
          history.push(`/facility-redirect/${redirectId}`);
        }, 2000);
      } else {
        const result = await api.facilityRedirects.createFacilityRedirect(payload);
        if (result.success === false) {
          setNotification(new Message('error', result.errorMessage));
          return;
        }
        setNotification(new Message('success', 'Redirect created successfully. Remember to restart the application.'));
        setTimeout(() => {
          history.push(`/facility-redirect/${result.id}`);
        }, 2000);
      }
    } catch (e) {
      console.error('Error saving redirect:', e);
      console.error('Error payload:', e.payload);
      console.error('Full error object:', JSON.stringify(e, null, 2));

      let errorMessage = 'Unable to save redirect';

      if (e.payload?.translatedMessage) {
        errorMessage = e.payload.translatedMessage;
      } else if (e.payload?.message) {
        errorMessage = e.payload.message;
      } else if (e.message && e.message !== 'Server returned error: ') {
        errorMessage = e.message.replace('Server returned error: ', '');
      }

      setNotification(new Message('error', errorMessage));
    }
  };

  const selectedType = parseInt(formData.Type);

  if (isLoading) {
    return <Loader hasLoaded={false}>Loading...</Loader>;
  }

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <PageTop
            documentTitle={isEditMode ? 'Edit Facility Redirect' : 'New Facility Redirect'}
            documentDescription={isEditMode ? 'Edit Facility Redirect' : 'New Facility Redirect'}
          />

          <React.Fragment>
            <h2 className='main-page-header main-page-header-with-subheader'>
              {isEditMode ? 'Edit Facility Redirect' : 'New Facility Redirect'}
            </h2>
            <h5 className='main-page-subheader'>
              <i>{isEditMode ? `Editing ${formData.SourceSlug}` : 'Create a new facility redirect'}</i>
            </h5>
            <br />
            <div style={containerStyle}>
              <form onSubmit={handleSubmit}>
                <div style={rowStyle}>
                  <label htmlFor="sourceSlug" style={formLabelStyle}>{"Source Slug*"}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="sourceSlug"
                      id="sourceSlug"
                      placeholder='e.g., old-facility-slug'
                      value={formData.SourceSlug}
                      onChange={({ target }) => setFormData({ ...formData, SourceSlug: target.value })}
                      style={{ width: '100%' }}
                    />
                    <div style={helpTextStyle}>
                      The original facility slug that needs to be redirected (for type 2: the old product slug)
                    </div>
                  </span>
                </div>
                <hr />

                <div style={rowStyle}>
                  <label htmlFor="type" style={formLabelStyle}>{'Redirect Type*'}</label>
                  <span style={formSpanStyle}>
                    <select
                      name="type"
                      id="type"
                      value={formData.Type}
                      onChange={({ target }) => setFormData({ ...formData, Type: target.value })}
                      style={{ width: '100%', padding: '5px' }}
                    >
                      <option value="1">Facility Slug Rename (1)</option>
                      <option value="2">Product Slug Rename (2)</option>
                      <option value="3">Full URL Redirect (3)</option>
                    </select>
                    <div style={helpTextStyle}>
                      Type 1: Redirect to another facility slug |
                      Type 2: Rename a product slug (Source Slug = old product slug) |
                      Type 3: Redirect to a custom URL
                    </div>
                  </span>
                </div>
                <hr />

                {(selectedType === 1 || selectedType === 2) && (
                  <>
                    <div style={rowStyle}>
                      <label htmlFor="targetSlug" style={formLabelStyle}>
                        {'Target Slug*'}
                      </label>
                      <span style={formSpanStyle}>
                        <input
                          name="targetSlug"
                          id="targetSlug"
                          placeholder='e.g., new-facility-slug'
                          value={formData.TargetSlug}
                          onChange={({ target }) => setFormData({ ...formData, TargetSlug: target.value })}
                          style={{ width: '100%' }}
                        />
                        <div style={helpTextStyle}>
                          {selectedType === 2
                            ? 'The new product slug to redirect to (Source Slug is the old product slug)'
                            : 'The new facility slug to redirect to'}
                        </div>
                      </span>
                    </div>
                    <hr />
                  </>
                )}

                {selectedType === 3 && (
                  <>
                    <div style={rowStyle}>
                      <label htmlFor="targetUrl" style={formLabelStyle}>
                        {'Target URL*'}
                      </label>
                      <span style={formSpanStyle}>
                        <input
                          name="targetUrl"
                          id="targetUrl"
                          placeholder='e.g., copenhagen/padel'
                          value={formData.TargetUrl}
                          onChange={({ target }) => setFormData({ ...formData, TargetUrl: target.value })}
                          style={{ width: '100%' }}
                        />
                        <div style={helpTextStyle}>
                          The full URL path to redirect to (without domain)
                        </div>
                      </span>
                    </div>
                    <hr />
                  </>
                )}

                {selectedType === 3 && (
                  <>
                    <div style={rowStyle}>
                      <label htmlFor="productSlug" style={formLabelStyle}>
                        {'Product Slug*'}
                      </label>
                      <span style={formSpanStyle}>
                        <input
                          name="productSlug"
                          id="productSlug"
                          placeholder='* for all products, or a product slug'
                          value={formData.ProductSlug}
                          onChange={({ target }) => setFormData({ ...formData, ProductSlug: target.value })}
                          style={{ width: '100%' }}
                        />
                        <div style={helpTextStyle}>
                          Required: "*" redirects every product URL of this facility; a product slug redirects only that product
                        </div>
                      </span>
                    </div>
                    <hr />
                  </>
                )}

                <div style={rowStyle}>
                  <label htmlFor="isActive" style={formLabelStyle}>{'Is Active'}</label>
                  <span style={formSpanStyle}>
                    <input
                      name="isActive"
                      id="isActive"
                      type="checkbox"
                      checked={formData.IsActive}
                      onChange={({ target }) => setFormData({ ...formData, IsActive: target.checked })}
                    />
                    <label htmlFor="isActive" style={{ marginLeft: '10px', fontWeight: 'normal' }}>
                      Enable this redirect
                    </label>
                  </span>
                </div>
                <hr />

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <ButtonOBSOLETED
                    buttonColor="primary"
                    type="submit"
                    buttonContent={isEditMode ? "Update Redirect" : "Create Redirect"}
                    ariaLabel={isEditMode ? "Update redirect" : "Create redirect"}
                    disabled={false}
                  />
                  <span style={{ marginLeft: '15px' }}>
                    <ButtonOBSOLETED
                      buttonColor="gray"
                      type="button"
                      onClick={() => history.push(isEditMode ? `/facility-redirect/${redirectId}` : '/facility-redirects')}
                      buttonContent="Cancel"
                      ariaLabel="Cancel"
                      disabled={false}
                    />
                  </span>
                </div>
              </form>
            </div>
          </React.Fragment>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

FacilityRedirectAddInternal.propTypes = {
  api: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

const FacilityRedirectAdd = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(FacilityRedirectAddInternal)
  )
);

FacilityRedirectAdd.requiresLogin = true;

export default FacilityRedirectAdd;
